/* eslint-disable @typescript-eslint/naming-convention */
import * as React from 'react';
import {
  GridReadyEvent,
  GridApi,
  ColDef,
  IRowNode,
  ValueFormatterParams,
  ColumnApi,
  CellValueChangedEvent,
  ICellRendererParams,
  CellRendererSelectorResult,
} from '@ag-grid-community/core';
import { isNil, isEmpty, partial, hasIn, findIndex, isEqual, flatten } from 'lodash';

import Button from '@material-ui/core/Button';
import Overlay from 'src/common-ui/components/Overlay/Overlay';

import * as styles from './AssortmentPublish.styles';
import { AssortmentPublishProps, AssortmentPublishState, AssortmentPublishSelection } from './AssortmentPublish.types';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import ExtendedDataGrid from 'src/components/ExtendedDataGrid/ExtendedDataGrid';
import { SubheaderOwnProps } from 'src/components/Subheader/Subheader.types';
import Subheader from 'src/components/Subheader/Subheader.container';
import { ASSORTMENT, IS_PUBLISHED } from 'src/utils/Domain/Constants';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { TenantConfigViewItem, TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { Renderer } from 'src/utils/Domain/Renderer';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import { DataGridProps } from 'src/common-ui/components/DataGrid/DataGrid';
import { getFrameworkComponents } from 'src/utils/Component/AgGrid/AgConfigParse';
import { toast } from 'react-toastify';
import { WorklistService } from 'src/services/Worklist/Worklist.service';

import container from 'src/ServiceContainer';
import { equals } from 'lodash/fp';
import { CoarseEditPayload } from 'src/dao/pivotClient';
import { getValidMembers } from 'src/pages/AssortmentStrategy/TargetSetting/Criteria/Criteria.client';
import CheckboxCellRenderer from '../StyleEdit/StyleEditSection/Renderers/Checkbox';
import { getPayloadValueFromInputParams } from '../FlowSheet/FlowSheet.utils';

class AssortmentPublish extends React.Component<AssortmentPublishProps, AssortmentPublishState> {
  state: AssortmentPublishState;
  gridApi!: GridApi;
  columnApi!: ColumnApi;

  constructor(props: AssortmentPublishProps) {
    super(props);

    this.state = {
      selectedItems: [],
      notificationModal: false,
    };
  }

  componentDidMount() {
    this.props.onShowView();
  }

  componentWillUnmount() {
    this.props.onDestroy();
  }

  componentDidUpdate(prevProps: AssortmentPublishProps) {
    if (
      !isEqual(prevProps.viewDefns.grid, this.props.viewDefns.grid) &&
      this.gridApi &&
      !isEmpty(this.props.viewDefns.grid)
    ) {
      this.gridApi.setColumnDefs(this.createColumnDefs());
    }

    // existence of props.dataApi is used here to magically detect that we're in allocation grid,
    // and not the assortment publish
    if (!equals(prevProps.data, this.props.data) && this.props.dataApi != null) {
      // NOTE: we don't save this to redux
      this.setState({
        selectedItems: this.props.data.map(this.basicPivotItemToAssortmentPublishSelection),
      });
    }
  }

  createColumnDefs(): ColDef[] {
    const { viewDefns } = this.props;
    const gridDefn = viewDefns.grid as TenantConfigViewData;
    return gridDefn.view
      .filter((colDef) => !colDef.hidden && colDef.visible !== false)
      .map(
        (colDef: any): ColDef => {
          let extraProps = {};

          if (colDef.dataIndex === 'checkboxHeader') {
            extraProps = {
              width: 35,
              headerCheckboxSelection: true,
              checkboxSelection: true,
              headerClass: styles.headerCheckbox,
            };
          }

          return {
            headerName: colDef.text || '',
            width: colDef.width ? colDef.width : undefined,
            field: colDef.dataIndex,
            pinned: colDef.pinned || false,

            valueFormatter: partial(this.handleValueFormatting, colDef),
            editable: false,
            cellRendererSelector: (params: ICellRendererParams): CellRendererSelectorResult => {
              if (!params || params.node == null) {
                return (null as unknown) as CellRendererSelectorResult;
              }
              switch (colDef.renderer) {
                case 'checkbox':
                  return {
                    component: 'checkboxCellRenderer',
                    params: {
                      isEditable: true,
                    },
                  };
                default:
                  return (null as unknown) as CellRendererSelectorResult;
              }
            },
            ...extraProps,
          };
        }
      );
  }

  handleValueFormatting = (colDef: TenantConfigViewItem, params: ValueFormatterParams) => {
    if (!isNil(colDef.renderer) && hasIn(Renderer, colDef.renderer)) {
      return Renderer.renderJustValue(params.value, colDef);
    }

    return params.value;
  };

  rowNodeToAssortmentPublishSelection = (node: IRowNode): AssortmentPublishSelection => {
    return {
      product: node.data.id,
      time: node.data.time,
    };
  };
  basicPivotItemToAssortmentPublishSelection = (item: BasicPivotItem): AssortmentPublishSelection => {
    return {
      product: item.id,
      time: item.time,
    };
  };

  handleCheck = () => {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const unfilteredNodes: IRowNode[] = [];

    // this api requries a callback for to get all the nodes
    this.gridApi.forEachNodeAfterFilter((n) => unfilteredNodes.push(n));

    const unfilteredSelectedNodes = selectedNodes.filter((n) => {
      // cross reference the selected and unfiltered nodes
      // users don't expect nodes that are filtered out to remain in their selection
      const idx = findIndex(unfilteredNodes, (nn) => {
        return nn.id === n.id;
      });
      return idx > -1;
    });

    if (this.props.onItemSelection) {
      const data: BasicPivotItem[] = unfilteredSelectedNodes.map((node) => {
        return node.data;
      });
      this.props.onItemSelection(data);
    }

    this.setState({
      selectedItems: unfilteredSelectedNodes.map(this.rowNodeToAssortmentPublishSelection),
    });
  };

  sendItems = async () => {
    const { viewDefns } = this.props;
    const { selectedItems, actionType } = this.state;

    if (isEmpty(selectedItems)) {
      return;
    }

    const publishAttribute = viewDefns.grid.publishAttribute || IS_PUBLISHED;
    const update = this.state.selectedItems.map((item) => {
      return {
        ...item,
        [publishAttribute]: actionType === 'publish' ? 1 : 0,
      };
    });

    if (this.props.submitPayload) {
      const payload = {
        keyField: ['product', 'time'],
        create: [],
        update,
        delete: [],
      };
      if (this.props.dataApi && this.props.dataApi.isListData && !isEmpty(this.props.data)) {
        // this is a new code path added for allocation
        // an arbitrary pivot needs to be called on publish completion,
        // then the worklist needs to be cleared
        // in this case, it is presumed that all items are checked
        try {
          const dataApi = this.props.dataApi;
          await container.pivotService.listData(dataApi.defnId, ASSORTMENT, dataApi.params);

          const itemIds = this.props.data.map((i) => i.product);
          const validSizeMembers = await Promise.all(
            this.props.data.map(async (item) => {
              const validMemberData = await getValidMembers({
                member: 'stylecolorsize',
                parent: item.id,
              });
              return validMemberData.map((sz: { id: string }) => {
                return { product: sz.id, location: item.location, time: item.time };
              });
            })
          );
          const sizeIds = flatten(validSizeMembers);

          // doomed
          const payload: CoarseEditPayload = {
            coordinates: sizeIds,
            holdback: null,
            ecom_reserve: null,
            store_reserve: null,
          };
          await container.pivotService.coarseEditSubmitData(payload);

          const worklistService = WorklistService();
          await worklistService.removeItemsFromWorklist(itemIds);
        } catch (e) {
          toast.error('An error occured publishing your assortment');
        } finally {
          toast(<div>Your allocations have been published, clearing your worklist now</div>, {
            position: toast.POSITION.TOP_RIGHT,
            type: 'info',
            toastId: 'clear-worklist',
          });
          this.setState({
            notificationModal: false,
            selectedItems: [],
          });
        }
        return;
      }
      this.props.submitPayload(payload).then(() => {
        this.setState({
          notificationModal: false,
          selectedItems: [],
        });
      });
    }
  };

  renderConfirmationModal = () => {
    return (
      <ConfirmationModal
        isOpen={this.state.notificationModal}
        descriptionText={`Are you sure you wish to ${this.state.actionType}?`}
        onConfirm={() => {
          this.sendItems();
          this.gridApi.deselectAll();
        }}
        onCancel={() => {
          this.gridApi.deselectAll();
          this.setState({
            notificationModal: false,
          });
        }}
      />
    );
  };

  render() {
    if (!this.props.configLoaded) {
      return <Overlay type="loading" visible={true} />;
    }

    const {
      title,
      dataLoaded,
      summary,
      subheaderErrorText,
      viewDefns,
      rowHeight,
      viewDataState,
      onUpdateConfig,
    } = this.props;
    const { selectedItems } = this.state;
    const viewConfiguratorProps = viewDefns.unmodifiedViewDefn && {
      unmodifiedViewDefn: viewDefns.unmodifiedViewDefn,
      viewConfig: viewDefns.grid,
      updateConfig: onUpdateConfig,
      showPinCheckboxForGrid: true,
      getColumnApi: () => {
        if (this.columnApi) {
          return this.columnApi;
        }
        return;
      },
    };
    const subheaderProps: SubheaderOwnProps = {
      title,
      showFlowStatus: true,
      showSearch: true,
      errorCondition: subheaderErrorText,
      viewConfigurator: viewConfiguratorProps,
      viewDataState,
    };

    const frameworkComponents = {
      ...getFrameworkComponents(),
      checkboxCellRenderer: CheckboxCellRenderer,
    };

    const gridProps: DataGridProps = {
      loaded: dataLoaded,
      className: 'publishGrid',
      rowHeight,
      data: this.props.data,
      columnDefs: this.createColumnDefs(),
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
        this.columnApi = params.columnApi;
      },
      exportOptions: {
        fileName: title,
      },
      frameworkComponents: frameworkComponents,
      extraAgGridProps: {
        onFilterChanged: () => {
          this.handleCheck();
        },
        headerHeight: 56,
        rowSelection: 'multiple',
        suppressRowClickSelection: true,
        icons: {
          checkboxChecked: '<i class="far fa-check-square" />',
          checkboxUnchecked: '<i class="far fa-square" />',
          checkboxIndeterminate: '<i class="far fa-circle" />',
          sortAscending: '<i class="fas fa-lg fa-sort-down"></i>',
          sortDescending: '<i class="fas fa-lg fa-sort-up"></i>',
          filter: '<i class="fas fa-filter"></i>',
        },
        onSelectionChanged: this.handleCheck,
        onCellValueChanged: async (params: CellValueChangedEvent) => {
          const { data: eventData, column, oldValue: oldVal } = params;
          const { value } = params;
          const colId = column.getColId();

          const maybeViewDefn = this.props.viewDefns.grid.view.find((c) => c.dataIndex === colId);
          if (!maybeViewDefn) {
            toast.error('An error occured trying to save your edit');
            throw new Error(`Couldn't find a viewdefn for the given column`);
          }

          if (
            this.props.submitPayload &&
            maybeViewDefn.editable &&
            maybeViewDefn.inputParams &&
            maybeViewDefn.inputParams.inputType &&
            typeof maybeViewDefn.inputParams.inputType === 'string'
          ) {
            const newValue = isNil(oldVal) || parseInt(oldVal) === 0 ? 1 : 0;

            const outgoingValue = getPayloadValueFromInputParams(newValue, maybeViewDefn.inputParams.inputType);

            this.props.submitPayload(
              {
                keyField: ['product', 'time'],
                create: [],
                update: [
                  {
                    product: eventData.id,
                    time: eventData.time,
                    [colId]: outgoingValue,
                  },
                ],
                delete: [],
              },
              true
            );
          }
          return value;
        },
      },
    };

    return (
      <div className={styles.viewContainer}>
        <Subheader {...subheaderProps} />
        <div className={styles.gridDetails}>
          <div>
            <span className={styles.gridCount}>Count: {gridProps.data.length}</span>
            <span className={styles.gridCount}>Selected: {selectedItems.length}</span>
          </div>
          <div>
            <span>{summary}</span>
          </div>
        </div>
        <div className={styles.gridWrapper}>
          <ExtendedDataGrid {...gridProps} />
        </div>
        <div className={styles.actionsContainer}>
          {this.props.dataApi == null ? (
            <Button
              onClick={() =>
                this.setState({
                  actionType: 'unpublish',
                  notificationModal: true,
                })
              }
              disabled={isEmpty(selectedItems)}
              className={styles.actionButton}
              disableFocusRipple={true}
            >
              Unpublish
            </Button>
          ) : null}
          <Button
            onClick={() =>
              this.setState({
                actionType: 'publish',
                notificationModal: true,
              })
            }
            disabled={isEmpty(selectedItems)}
            className={styles.actionButton}
            disableFocusRipple={true}
          >
            {this.props.viewDefns.grid.publishText || 'Publish'}
          </Button>
        </div>
        {this.renderConfirmationModal()}
      </div>
    );
  }
}

export default makeScopeAndFilterSensitive(makePrintSensitive(AssortmentPublish));
